import React from 'react';

import { EnhancedDisplayType } from '@components/widgets/dynamic-display/EnhancedDisplay';
import { IEntityInfoView } from '@components/widgets/entity-info/EntityInfoFields';
import EntityInfoWidget from '@components/widgets/entity-info/EntityInfoWidget';
import { useAppSelector } from '@core/redux/hooks';
import { MAIN_THEME } from '@styles';
import { useUserFullNameById } from '@utils/helper-utils';

import { AmendmentDetails } from '../types';

type Props = {
  details: AmendmentDetails;
};

const LeftPanelAtom: React.FC<Props> = ({ details }) => {
  const user = useAppSelector((state) => state.user);

  const roles = user?.updatedUserRoles ?? [];
  const isAdminOrUnderwriter = roles.some((r: string) =>
    ['Admin', 'Underwriter'].includes(r)
  );

  const s = details.summaryDetails;
  const policyType = details.policyDetails?.policyType;
  const isGroupPolicy = policyType === 'GROUP';
  const amendmentSubTypeCode =
    details.amendmentAmendmentDetails?.amendmentSubType?.code;
  const showCertificateNumber =
    amendmentSubTypeCode === 'EC10' || amendmentSubTypeCode === 'EC11';
  const certificateSummary = details.certificateAmendment?.summaryDetails;

  const policyId = s.policyId;
  const lineId = s.lineId;
  const sublineId = s.sublineId;
  const planId = s.planId;

  const createdByName = useUserFullNameById(s.createdBy);
  const modifiedByName = useUserFullNameById(s.modifiedBy);

  const title = s.referenceNumber;

  const view: IEntityInfoView = {
    iconUrl: '',
    name: 'Amendment',
    socialMedia: { facebook: '', twitter: '', linkedIn: '' },
    title,
    sections: [
      {
        name: 'BasicInformation',
        title: '',
        properties: {
          PolicyNumber: {
            multiline: false,
            value: s.policyNumber,
            name: 'PolicyNumber',
            title: isGroupPolicy ? 'Group Policy' : 'Policy Number',
            type: EnhancedDisplayType.RelativeLink,
            url: `/production/${isGroupPolicy ? 'group-policies' : 'policies'}/${policyId}`
          },
          CertificateNumber: {
            multiline: false,
            value: certificateSummary?.policyNumber,
            name: 'CertificateNumber',
            title: 'Certificate Number',
            hidden: !showCertificateNumber,
            type: EnhancedDisplayType.RelativeLink,
            url: certificateSummary?.policyId
              ? `/production/policies/${certificateSummary.policyId}`
              : undefined
          },
          Line: {
            multiline: false,
            value: s.line,
            name: 'Line',
            title: 'Line',
            type: isAdminOrUnderwriter
              ? EnhancedDisplayType.RelativeLink
              : undefined,
            url: isAdminOrUnderwriter ? `/plan/lines/${lineId}` : undefined
          },
          Subline: {
            multiline: false,
            value: s.subline,
            name: 'Subline',
            title: 'Subline',
            type: isAdminOrUnderwriter
              ? EnhancedDisplayType.RelativeLink
              : undefined,
            url: isAdminOrUnderwriter
              ? `/plan/sublines/${sublineId}`
              : undefined
          },
          Plan: {
            multiline: false,
            value: s.plan,
            name: 'Plan',
            title: 'Plan',
            type: isAdminOrUnderwriter
              ? EnhancedDisplayType.RelativeLink
              : undefined,
            url: isAdminOrUnderwriter ? `/plan/plans/${planId}` : undefined
          },
          RelatedCompany: {
            multiline: false,
            value: s.relatedCompany,
            name: 'RelatedCompany',
            title: 'Related Company'
          }
        }
      },
      {
        name: 'GeneralInformation',
        title: '',
        properties: {
          BusinessUser: {
            multiline: false,
            value: s.user,
            name: 'BusinessUser',
            title: 'User'
          },
          MainBP: {
            multiline: false,
            value: s.mainBusinessPartner,
            name: 'MainBP',
            title: 'Main Broker'
          }
        }
      },
      {
        name: 'SystemInformation',
        title: '',
        properties: {
          CreatedBy: {
            multiline: false,
            value: createdByName,
            name: 'CreatedBy',
            title: 'Created By'
          },
          CreatedOn: {
            multiline: false,
            value: s.createdOn,
            name: 'CreatedOn',
            title: 'Created On',
            type: EnhancedDisplayType.DateTime
          },
          ModifiedBy: {
            multiline: false,
            value: modifiedByName,
            name: 'ModifiedBy',
            title: 'Modified By'
          },
          ModifiedOn: {
            multiline: false,
            value: s.modifiedOn,
            name: 'ModifiedOn',
            title: 'Modified On',
            type: EnhancedDisplayType.DateTime
          }
        }
      }
    ]
  };

  return (
    <EntityInfoWidget
      background={MAIN_THEME.color.red}
      classification={{ classifications: {}, value: '' }}
      progress={100}
      supportsClassification
      supportsProgressBar
      title={title}
      view={view}
    />
  );
};

export { LeftPanelAtom };
